/** @format */

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  LanguageSettings,
  MainTemplatesLayout,
  ParticipationModule,
  ProjectTemplate,
  Skeleton,
  TagSlide,
  useIsMobile,
} from "senf-atomic-design-system";
import {
  formatDateBasedOnLanguage,
  getPhaseOfDateRange,
  useAuthContext,
  useGlobalState,
} from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import { cn } from "../../../../util/classNameConstructor";
import Button from "../../../core/button/Button";
import Seperator from "../../../core/seperator/Seperator";
import TooltipProvider from "../../../core/tooltip/TooltipProvider";
import LanguageButton from "../../../molecules/languageSettings/LanguageButton";
import { useModals } from "../../../molecules/modalStack/ModalProvider";
import PostIdeaButton from "../../../pages/postIdea/PostIdeaButton";
import AccessibilitySettings from "../../accessibilitySettings/AccessibilitySettings";

// Custom hook for dynamic header height calculation
const useHeaderHeight = (headerRef) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isMeasured, setIsMeasured] = useState(false);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header =
        headerRef.current || document.getElementById("ideaMapListHeader");

      if (header) {
        // Use getBoundingClientRect for more accurate measurement
        const rect = header.getBoundingClientRect();
        const height = Math.round(rect.height);

        if (height > 0 && height !== headerHeight) {
          setHeaderHeight(height);
          setIsMeasured(true);
        }
      }
    };

    // Initial measurement with requestAnimationFrame for better timing
    const initialMeasure = () => {
      requestAnimationFrame(() => {
        updateHeaderHeight();
        // Retry once more after a short delay if still not measured
        if (!isMeasured) {
          setTimeout(updateHeaderHeight, 50);
        }
      });
    };

    // Set up ResizeObserver immediately
    const setupResizeObserver = () => {
      const header =
        headerRef.current || document.getElementById("ideaMapListHeader");

      if (header && !resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const height = Math.round(entry.contentRect.height);
            if (height > 0 && height !== headerHeight) {
              setHeaderHeight(height);
              setIsMeasured(true);
            }
          }
        });
        resizeObserverRef.current.observe(header);
      }
    };

    // Start measurement process
    initialMeasure();
    setupResizeObserver();

    // Fallback measurement for edge cases
    const fallbackTimer = setTimeout(updateHeaderHeight, 200);

    // Cleanup
    return () => {
      clearTimeout(fallbackTimer);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [headerRef, headerHeight, isMeasured]);

  return { headerHeight, isMeasured };
};

const IdeaMapList = ({
  project,
  ideaMap,
  handle,
  projectIdeas,
  filteredProjectIdeas,
  setSelectedArealFilter,
  selectedArealFilter,
  open,
  setOpen,
  setPosition,
  checkedSortOption,
  setCheckedSortOption,
  searchTerm,
  setSearchTerm,
  activeFilters,
  setActiveFilters,
  isMobile: isMobileProp,
  preview,
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const { ideaId, organizationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const profileId = searchParams.get("profile");
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const isMobileFallback = useIsMobile();
  const isMobile = isMobileProp ?? isMobileFallback;
  const [searchOpen, setSearchOpen] = useState(false);
  const { openModal } = useModals();

  // Header height measurement
  const headerRef = useRef(null);
  const { headerHeight, isMeasured } = useHeaderHeight(headerRef);

  // Calculate dynamic bottom position
  const dynamicBottomPosition = Math.max(195, headerHeight + 135);

  // Force re-render when header is measured
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    if (isMeasured && headerHeight > 0) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [isMeasured, headerHeight]);

  // Use measured height with fallback to minimum
  const effectiveHeaderHeight = Math.max(20, headerHeight);
  const effectiveBottomPosition = Math.max(195, effectiveHeaderHeight + 158);

  const arealFiltersPre = [
    { name: "spacing" },
    ideaMap?.addIdeaOptions?.drawPoint && {
      leadingIcon: "pin",
      value: "location",
      label: t("dialogMap:add.withLocationShort"),
    },
    ideaMap?.addIdeaOptions?.generalIdeas && {
      leadingIcon: "pin-strikethrough",
      value: "noLocation",
      label: t("dialogMap:add.withoutLocationShort"),
    },
    ideaMap?.addIdeaOptions?.drawPath && {
      leadingIcon: "path",
      value: "path",
      label: t("global:paths"),
    },
    ideaMap?.addIdeaOptions?.drawArea && {
      leadingIcon: "area",
      value: "area",
      label: t("global:areas"),
    },
  ].filter(Boolean);
  const arealFilters = [
    ...(arealFiltersPre.length > 2 ? arealFiltersPre : []),
    { name: "spacing" },
    {
      leadingIcon: "map",
      value: "inMapArea",
      label: t("global:inMapArea"),
    },
  ].filter(Boolean);

  arealFilters.filter((e) => e.value).length;

  useEffect(() => {
    if (
      ideaMap?.startDate &&
      getPhaseOfDateRange(ideaMap?.startDate, ideaMap?.endDate) !== "while"
    ) {
      setCheckedSortOption?.("hottest");
    }
  }, [ideaMap?.startDate, ideaMap?.endDate, setCheckedSortOption]);

  // useEffect(() => {
  //   setActiveFilters("topics", []);
  // }, []);

  const catchRef = useRef();

  const [scrollBumped, setScrollBumped] = useState(false);
  const scrollHandler = () => {
    if (!isMobile) return;
    const stickyElm = document.getElementById("scrollBumpeRef");
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle("isSticky", e.intersectionRatio < 1),
      { threshold: [1] },
    );
    observer.observe(stickyElm);
    if (catchRef?.current?.classList?.contains("isSticky")) {
      setScrollBumped(true);
    } else {
      setScrollBumped(false);
    }
  };

  return (
    <React.Fragment>
      {isMobile && (
        <nav>
          {project?.infoPages?.length
            ? pathname.indexOf("addIdea") === -1 &&
              pathname.indexOf("editIdea") === -1 &&
              !searchParams?.get("profile") &&
              !ideaId && (
                <Button
                  text={t("global:infoPage")}
                  leadingIcon="arrowhead-left-md"
                  variant="outline"
                  className="rounded-full bg-white shadow-xl"
                  classNameWrapper="fixed top-5 z-10 start-5"
                  onClick={() => handle.openInfo()}
                  aria-label={t("util:toThe", { val: t("global:infoPage") })}
                />
              )
            : null}
          {project?.settings?.languages?.length &&
          pathname.indexOf("info") === -1 &&
          pathname.indexOf("addIdea") === -1 &&
          pathname.indexOf("editIdea") === -1 ? (
            <LanguageButton
              project={project}
              className="shadow-xl"
              classNameWrapper="fixed top-5 z-10 end-16"
            />
          ) : null}
        </nav>
      )}

      {project && !isMobile && (
        <div
          className={cn("pointer-events-none fixed top-0 m-2.5 start-[436px]")}
        >
          <TagSlide
            direction="vertical"
            type={"topic"}
            tags={ideaMap?.customTopics}
            handle={handle}
            hide={
              ideaId ||
              profileId ||
              (organizationId && !preview) ||
              pathname.includes("addIdea") ||
              pathname.includes("editIdea")
            }
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            arealFilters={arealFilters}
            selectedArealFilters={selectedArealFilter}
            setSelectedArealFilter={setSelectedArealFilter}
            className="absolute"
          />
        </div>
      )}

      <MainTemplatesLayout
        key={`layout-${forceUpdate}-${effectiveHeaderHeight}`} // Force re-render when header height changes
        onScroll={scrollHandler}
        options={{
          openTo: "bottom",
          open,
          setPosition,
          setOpen,
          positions: {
            top: window.innerHeight - 10,
            middle: window.innerHeight - 10,
            bottom: effectiveBottomPosition, // Dynamic bottom position based on header height
          },
        }}
        // onPositionChange={(position) => {
        //   console.log("position", position);
        // }}
        mainHeader={true}
        withHeaderBorder={!ideaMap?.interactionOptions?.allowPosts && !isMobile}
        header={
          <header
            ref={headerRef}
            className={cn(
              "h-auto min-h-70 flex w-full flex-col items-start justify-start gap-2",
              !isMobile && "py-4",
            )}
            id="ideaMapListHeader"
          >
            {!isMobile && (
              <nav className="flex flex-wrap gap-2 mb-2">
                {project?.infoPages?.length ? (
                  <Button
                    text={t("global:infoPage")}
                    leadingIcon="arrowhead-left-md"
                    variant="outline"
                    onClick={() => handle.openInfo()}
                    aria-label={t("util:toThe", { val: t("global:infoPage") })}
                  />
                ) : null}
                {project?.settings?.languages?.length ? (
                  <LanguageButton project={project} />
                ) : null}
                {project?.settings?.accessibility ? (
                  <AccessibilitySettings project={project} />
                ) : null}
              </nav>
            )}
            {/* <Seperator className="-mx-6" /> */}
            {/* {handle.openInfo ? (
              <Button
                variant="shapeless"
                leadingIcon="ArrowLeft"
                onClick={() => handle.openInfo()}
              />
            ) : (
              <div className="w-2" />
            )} */}

            <div
              className={cn(
                "flex flex-col gap-2",
                !isMobile && ideaMap?.subtitle && "pb-2",
              )}
            >
              {ideaMap?.title || project?.title ? (
                <h2
                  className={cn(
                    "heading-lg pointer-events-auto [text-wrap:balance] leading-tight mx-1",
                    scrollBumped && "body-md-bold",
                    !isMobile && "max-w-[380px]", // Constrain width on desktop
                  )}
                  aria-label="tooltip"
                >
                  {translation?.[ideaMap?.title] ||
                    ideaMap?.title ||
                    project?.title}
                </h2>
              ) : (
                <Skeleton className="heading-lg" />
              )}
              {ideaMap?.subtitle && (
                <span
                  className={cn(
                    "body-md text-text-subdued leading-tight",
                    !isMobile && "max-w-[380px]", // Constrain width on desktop
                  )}
                >
                  {translation?.[ideaMap?.subtitle] || ideaMap?.subtitle}
                </span>
              )}
            </div>
          </header>
        }
        footer={
          ideaMap?.interactionOptions?.allowPosts ? (
            <PostIdeaButton ideaMap={ideaMap} handle={handle} />
          ) : null
        }
      >
        {project && isMobile ? (
          <div className="absolute z-[999] m-0 -mt-1 h-[45px] w-[var(--w-screen,100vw)] bg-white transition-all duration-100">
            <TagSlide
              direction={"horizontal"}
              type={"topic"}
              tags={ideaMap?.customTopics}
              hide={
                ideaId ||
                profileId ||
                (organizationId && !preview) ||
                pathname.includes("addIdea") ||
                pathname.includes("editIdea")
              }
              handle={handle}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              arealFilters={
                arealFilters
                // ideaMap?.addIdeaOptions?.generalIdeas
                //   ? arealFilters
                //   : arealFilters.filter(
                //       (arealFilter) => arealFilter.value === "inMapArea"
                //     )
              }
              setSelectedArealFilter={setSelectedArealFilter}
              selectedArealFilters={selectedArealFilter}
              className="absolute"
            />
          </div>
        ) : ideaMap?.interactionOptions?.allowPosts ? (
          <div className="sticky top-0 z-50 bg-white px-5">
            <PostIdeaButton ideaMap={ideaMap} handle={handle} />
          </div>
        ) : null}

        <ProjectTemplate
          user={user}
          ideaMap={ideaMap}
          data={project}
          handle={handle}
          filteredIdeasData={filteredProjectIdeas}
          ideasData={projectIdeas}
          setSearchOpen={setSearchOpen}
          searchOpen={searchOpen}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          checkedSortOption={checkedSortOption}
          setCheckedSortOption={setCheckedSortOption}
          catchRef={catchRef}
        />
      </MainTemplatesLayout>
    </React.Fragment>
  );
};

export default IdeaMapList;
